// src/components/Register Components/Register.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../Context/AuthContext';
import { FiSun, FiMoon, FiUser, FiLock, FiSettings, FiEye, FiEyeOff, FiGlobe, FiZap, FiShield, FiMail, FiPhone, FiCheck, FiX, FiGift, FiKey, FiLoader, FiSlash, FiUserCheck, FiTrendingUp, FiAlertTriangle, FiArrowLeft, FiArrowRight, FiCheckCircle, FiShoppingBag } from 'react-icons/fi';
import { FaUserAstronaut, FaCoins } from 'react-icons/fa';
import NeonCity from '../CityFX/NeonCity';
import WalletAuthButton from '../WalletSecurity/WalletAuthButton';
import './Register.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000';
// El alta por wallet solo se ofrece si hay App ID de Privy configurado.
const WALLET_ENABLED = !!process.env.REACT_APP_PRIVY_APP_ID;

const COUNTRIES = [
  { code: 'CR',    label: 'Costa Rica'     },
  { code: 'CO',    label: 'Colombia'       },
  { code: 'VE',    label: 'Venezuela'      },
  { code: 'MX',    label: 'México'         },
  { code: 'AR',    label: 'Argentina'      },
  { code: 'PE',    label: 'Perú'           },
  { code: 'CL',    label: 'Chile'          },
  { code: 'BR',    label: 'Brasil'         },
  { code: 'EC',    label: 'Ecuador'        },
  { code: 'BO',    label: 'Bolivia'        },
  { code: 'PY',    label: 'Paraguay'       },
  { code: 'UY',    label: 'Uruguay'        },
  { code: 'PA',    label: 'Panamá'         },
  { code: 'US',    label: 'Estados Unidos' },
  { code: 'ES',    label: 'España'         },
  { code: 'other', label: 'Otro'           },
];

const CRYPTOS = ['BTC', 'ETH', 'USDT', 'SOL', 'BNB', 'LTC'];

const STEPS = [
  { id: 1, icon: FiUser, label: 'Perfil'       },
  { id: 2, icon: FiLock, label: 'Seguridad'    },
  { id: 3, icon: FiSettings, label: 'Preferencias' },
];

function getPasswordStrength(pw) {
  if (!pw) return { score: 0, label: '', color: '' };
  let score = 0;
  if (pw.length >= 8)           score++;
  if (/[A-Z]/.test(pw))         score++;
  if (/[0-9]/.test(pw))         score++;
  if (/[^A-Za-z0-9]/.test(pw))  score++;
  const levels = [
    { score: 0, label: '',        color: ''        },
    { score: 1, label: 'Débil',   color: '#e53935' },
    { score: 2, label: 'Regular', color: '#f0b90b' },
    { score: 3, label: 'Buena',   color: '#0091ea' },
    { score: 4, label: 'Fuerte',  color: '#00d412' },
  ];
  return levels[score];
}

const CRYPTO_SYMBOLS = ['₿', 'Ξ', '◎', '◈', '₮', '⬡'];

function GamingBackground() {
  return (
    <div className="rg-bg" aria-hidden="true">
      {/* Capas cyberpunk: estrellas + grilla synthwave + skyline (solo tema oscuro) */}
      <NeonCity prefix="rg" />
      <div className="rg-orb rg-orb-1" />
      <div className="rg-orb rg-orb-2" />
      <div className="rg-orb rg-orb-3" />

      {Array.from({ length: 30 }).map((_, i) => (
        <div
          key={`p-${i}`}
          className="rg-particle"
          style={{
            left: `${Math.random() * 100}%`,
            animationDuration: `${6 + Math.random() * 10}s`,
            animationDelay: `${Math.random() * 8}s`,
            width: `${1.5 + Math.random() * 2.5}px`,
            height: `${1.5 + Math.random() * 2.5}px`,
            opacity: 0.15 + Math.random() * 0.35,
          }}
        />
      ))}

      {Array.from({ length: 8 }).map((_, i) => (
        <div
          key={`c-${i}`}
          className="rg-crypto-float"
          style={{
            left: `${8 + (i * 12) % 85}%`,
            animationDuration: `${14 + Math.random() * 12}s`,
            animationDelay: `${i * 1.8}s`,
            fontSize: `${0.7 + Math.random() * 0.6}rem`,
          }}
        >
          {CRYPTO_SYMBOLS[i % CRYPTO_SYMBOLS.length]}
        </div>
      ))}
    </div>
  );
}

export default function Register() {
  const navigate  = useNavigate();
  const { login } = useAuth();

  const [step, setStep]       = useState(1);
  const [error, setError]     = useState('');
  const [loading, setLoading] = useState(false);
  const [showPw, setShowPw]   = useState(false);

  const [inviteStatus, setInviteStatus] = useState(null);
  const [checkingInvite, setCheckingInvite] = useState(false);

  const [darkMode, setDarkMode] = useState(() => {
    const saved = localStorage.getItem('bitplify_theme');
    return saved ? saved === 'dark' : true;
  });

  const toggleTheme = () => {
    const next = !darkMode;
    setDarkMode(next);
    localStorage.setItem('bitplify_theme', next ? 'dark' : 'light');
    document.body.classList.toggle('dark-mode', next);
  };

  useEffect(() => {
    document.body.classList.toggle('dark-mode', darkMode);
  }, [darkMode]);

  const [form, setForm] = useState({
    name: '', username: '', email: '', country: 'CR', phone: '',
    password: '', confirmPassword: '',
    favCrypto: 'BTC', referral: '',
    inviteCode: '',
    acceptTerms: false,
  });

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const pwStrength = getPasswordStrength(form.password);

  // Link de referido de un comerciante: ?ref=slug → se atribuye al registrarse
  const [refParam, setRefParam] = useState(null);
  useEffect(() => {
    try { const r = new URLSearchParams(window.location.search).get('ref'); if (r) setRefParam(r); } catch {}
  }, []);

  const handleInviteCodeChange = async (val) => {
    const code = val.toUpperCase();
    set('inviteCode', code);
    if (code.length < 8) { setInviteStatus(null); return; }
    setCheckingInvite(true);
    try {
      const res  = await fetch(`${API_URL}/api/invite/${code}`);
      const data = await res.json();
      setInviteStatus(data.valid ? 'valid' : 'invalid');
    } catch {
      setInviteStatus(null);
    }
    setCheckingInvite(false);
  };

  const validateStep = () => {
    if (step === 1) {
      if (!form.name.trim())                        return 'Ingresa tu nombre.';
      if (!form.username.trim())                    return 'Elige un nombre de usuario.';
      if (form.username.length < 3)                 return 'El nombre de usuario debe tener al menos 3 caracteres.';
      if (!/^[a-zA-Z0-9_]+$/.test(form.username))  return 'Solo letras, números y guion bajo.';
      if (!form.email.trim())                       return 'Ingresa tu email.';
      if (!/\S+@\S+\.\S+/.test(form.email))         return 'Ingresa un email válido.';
      if (!form.phone.trim())                       return 'Ingresa tu teléfono.';
      if (form.phone.replace(/\D/g, '').length < 7) return 'Ingresa un teléfono válido.';
      if (!form.country)                            return 'Selecciona tu país.';
    }
    if (step === 2) {
      if (!form.password)                          return 'Crea una contraseña.';
      if (form.password.length < 8)                return 'Mínimo 8 caracteres.';
      if (form.password !== form.confirmPassword)  return 'Las contraseñas no coinciden.';
      if (!form.acceptTerms)                       return 'Acepta los términos de servicio.';
    }
    return '';
  };

  const handleNext = () => {
    const err = validateStep();
    if (err) { setError(err); return; }
    setError('');
    setStep(s => s + 1);
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/register`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name:       form.name,
          username:   form.username,
          email:      form.email,
          phone:      form.phone      || null,
          password:   form.password,
          country:    form.country,
          favCrypto:  form.favCrypto,
          referral:   form.referral   || null,
          inviteCode: form.inviteCode || null,
          ref:        refParam        || null,   // atribución por link de referido (?ref=slug)
        }),
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.message || `Error ${res.status}`);
      }

      const data = await res.json();
      if (data.token) {
        login(data.user, data.token);
        setStep(4);
      } else {
        setError('No se pudo completar el registro.');
      }
    } catch (err) {
      setError(err.message === 'Failed to fetch'
        ? 'No se pudo conectar al servidor. Intenta más tarde.'
        : err.message || 'Error al registrarse.'
      );
    } finally {
      setLoading(false);
    }
  };

  const selectedCountry = COUNTRIES.find(c => c.code === form.country) ?? COUNTRIES[0];

  return (
    <div className={`rg-wrapper ${darkMode ? 'dark' : 'light'}`}>
      <GamingBackground />

      <button className="rg-theme-toggle" onClick={toggleTheme} title={darkMode ? 'Modo claro' : 'Modo oscuro'}>
        <span className="rg-theme-icon">
          {darkMode ? <FiSun /> : <FiMoon />}
        </span>
      </button>

      <div className="rg-left">
        <div className="rg-brand">
          <img src="/assets/Logo-White.png" alt="Bitplify" className="rg-logo rg-logo-dark" />
          <img src="/assets/Logo.png"       alt="Bitplify" className="rg-logo rg-logo-light" />
          <span className="rg-beta">Beta</span>
        </div>
        <div className="rg-hero">
          <h1 className="rg-hero-title">El protagonista sos vos,<br />comienza la aventura aquí.</h1>
          <p className="rg-hero-sub">Crea tu cuenta en minutos y empieza a operar P2P en LATAM y el mundo.</p>
        </div>
        <div className="rg-perks">
          {[
            { Icon: FiShield, text: 'Tu plata en tu wallet — vos tenés la llave' },
            { Icon: FiUserCheck, text: 'Solo vendedores verificados por invitación' },
            { Icon: FiZap, text: 'Trades en menos de 5 minutos'          },
            { Icon: FiTrendingUp, text: 'Subí de nivel con cada operación'      },
          ].map((p, i) => (
            <div key={i} className="rg-perk">
              <span className="rg-perk-icon"><p.Icon size={20} /></span>
              <span>{p.text}</span>
            </div>
          ))}
        </div>
        <p className="rg-left-footer"><FiTrendingUp className="rg-footer-icon" /> Intercambia crypto. Pon tu precio. Sube de nivel.</p>
      </div>

      <div className="rg-right">
        <div className="rg-card">

          <div className="rg-mobile-brand">
            <img src="/assets/Logo-White.png" alt="Bitplify" className="rg-mobile-logo rg-logo-dark" />
            <img src="/assets/Logo.png"       alt="Bitplify" className="rg-mobile-logo rg-logo-light" />
            <span className="rg-beta">Beta</span>
          </div>

          {step < 4 && (
            <>
              <div className="rg-card-header">
                <h2 className="rg-card-title">Crear cuenta</h2>
                <p className="rg-card-sub">Paso {step} de 3</p>
              </div>
              <div className="rg-stepper">
                {STEPS.map((s, i) => (
                  <React.Fragment key={s.id}>
                    <div className={`rg-step ${step === s.id ? 'active' : ''} ${step > s.id ? 'done' : ''}`}>
                      <div className="rg-step-circle">{step > s.id ? <FiCheck size={16} /> : <s.icon size={16} />}</div>
                      <span className="rg-step-label">{s.label}</span>
                    </div>
                    {i < 2 && <div className={`rg-step-line ${step > s.id ? 'done' : ''}`} />}
                  </React.Fragment>
                ))}
              </div>
            </>
          )}

          {step === 1 && (
            <div className="rg-form">
              <div className="rg-field">
                <label className="rg-label"><FiUser className="rg-label-icon" /> Nombre completo</label>
                <input className="rg-input" placeholder="Tu nombre" value={form.name} onChange={e => set('name', e.target.value)} />
              </div>
              <div className="rg-field">
                <label className="rg-label"><FaUserAstronaut className="rg-label-icon" /> Nombre de trader</label>
                <div className="rg-prefix-wrap">
                  <span className="rg-prefix">@</span>
                  <input className="rg-input" placeholder="NombreCool" value={form.username} onChange={e => set('username', e.target.value.replace(/\s/g, ''))} />
                </div>
                <span className="rg-hint">Solo letras, números y guion bajo. Así te verán en el marketplace.</span>
              </div>
              <div className="rg-field">
                <label className="rg-label"><FiMail className="rg-label-icon" /> Email</label>
                <input className="rg-input" type="email" placeholder="trader@email.com" value={form.email} onChange={e => set('email', e.target.value)} />
              </div>
              <div className="rg-field">
                <label className="rg-label"><FiPhone className="rg-label-icon" /> Teléfono</label>
                <input className="rg-input" type="tel" placeholder="+506 8888 8888" value={form.phone} onChange={e => set('phone', e.target.value)} />
                <span className="rg-hint">Lo usamos para verificar tu cuenta y avisarte de tus trades.</span>
              </div>
              <div className="rg-field">
                <label className="rg-label"><FiGlobe className="rg-label-icon" /> País</label>
                <select className="rg-select" value={form.country} onChange={e => set('country', e.target.value)}>
                  {COUNTRIES.map(c => (
                    <option key={c.code} value={c.code}>{c.label}</option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {/* Alternativa: crear tu cuenta con wallet (secundario · autocustodia) */}
          {step === 1 && WALLET_ENABLED && (
            <div className="rg-wallet">
              <div className="rg-wallet-divider"><span>o creá tu wallet (autocustodia)</span></div>
              <WalletAuthButton label="Crear mi wallet" onSuccess={() => navigate('/')} />
              <p style={{ margin: '2px 0 0', textAlign: 'center', fontSize: '0.72rem', color: 'var(--mk-text-tertiary, #8a9bb0)', lineHeight: 1.4 }}>
                Es <b>una sola cuenta</b> — elegí una forma ahora y sumás la otra después, para respaldo.
              </p>
            </div>
          )}

          {step === 2 && (
            <div className="rg-form">
              <div className="rg-field">
                <label className="rg-label"><FiLock className="rg-label-icon" /> Contraseña</label>
                <div className="rg-pw-wrap">
                  <input
                    className="rg-input"
                    type={showPw ? 'text' : 'password'}
                    placeholder="Mínimo 8 caracteres"
                    value={form.password}
                    onChange={e => set('password', e.target.value)}
                  />
                  <button type="button" className="rg-pw-toggle" onClick={() => setShowPw(p => !p)}>
                    {showPw ? <FiEyeOff /> : <FiEye />}
                  </button>
                </div>
                {form.password && (
                  <div className="rg-strength">
                    <div className="rg-strength-bar">
                      {[1,2,3,4].map(n => (
                        <div key={n} className="rg-strength-seg" style={{ background: n <= pwStrength.score ? pwStrength.color : 'var(--rg-border)' }} />
                      ))}
                    </div>
                    <span className="rg-strength-label" style={{ color: pwStrength.color }}>{pwStrength.label}</span>
                  </div>
                )}
                <div className="rg-pw-rules">
                  {[
                    { ok: form.password.length >= 8,          text: '8+ caracteres' },
                    { ok: /[A-Z]/.test(form.password),         text: 'Mayúscula'     },
                    { ok: /[0-9]/.test(form.password),         text: 'Número'        },
                    { ok: /[^A-Za-z0-9]/.test(form.password),  text: 'Símbolo'       },
                  ].map((r, i) => (
                    <span key={i} className={`rg-pw-rule ${r.ok ? 'ok' : ''}`}>
                      {r.ok
                        ? <FiCheck size={13} className="rg-pw-rule-icon" />
                        : <FiX size={13} className="rg-pw-rule-icon" />} {r.text}
                    </span>
                  ))}
                </div>
              </div>
              <div className="rg-field">
                <label className="rg-label"><FiLock className="rg-label-icon" /> Confirmar contraseña</label>
                <input
                  className="rg-input"
                  type="password"
                  placeholder="Repite tu contraseña"
                  value={form.confirmPassword}
                  onChange={e => set('confirmPassword', e.target.value)}
                />
                {form.confirmPassword && (
                  <span className={`rg-match ${form.password === form.confirmPassword ? 'ok' : 'fail'}`}>
                    {form.password === form.confirmPassword ? <><FiCheck size={14} style={{marginRight: '4px'}} /> Coinciden</> : <><FiX size={14} style={{marginRight: '4px'}} /> No coinciden</>}
                  </span>
                )}
              </div>
              <label className="rg-terms">
                <input type="checkbox" checked={form.acceptTerms} onChange={e => set('acceptTerms', e.target.checked)} />
                <span>Acepto los <a href="/terms" target="_blank" rel="noopener noreferrer">Términos</a> y la <a href="/privacy" target="_blank" rel="noopener noreferrer">Privacidad</a></span>
              </label>
            </div>
          )}

          {step === 3 && (
            <div className="rg-form">
              <p className="rg-step-hint">Último paso — personaliza tu experiencia de trading.</p>
              <div className="rg-field">
                <label className="rg-label"><FiTrendingUp className="rg-label-icon" /> Cripto favorita</label>
                <div className="rg-crypto-grid">
                  {CRYPTOS.map(c => (
                    <button
                      key={c}
                      type="button"
                      className={`rg-crypto-btn ${form.favCrypto === c ? 'active' : ''}`}
                      onClick={() => set('favCrypto', c)}
                    >{c}</button>
                  ))}
                </div>
              </div>

              <div className="rg-field">
                <label className="rg-label"><FiKey className="rg-label-icon" /> Código de invitación <span className="rg-optional">(opcional)</span></label>
                <div className="rg-invite-wrap">
                  <input
                    className={`rg-input rg-invite-input ${inviteStatus === 'valid' ? 'valid' : inviteStatus === 'invalid' || inviteStatus === 'used' ? 'invalid' : ''}`}
                    placeholder="BETA-XXXXXXXX"
                    value={form.inviteCode}
                    onChange={e => handleInviteCodeChange(e.target.value)}
                    maxLength={14}
                  />
                  {checkingInvite && <span className="rg-invite-status"><FiLoader className="rg-invite-spin" /></span>}
                  {!checkingInvite && inviteStatus === 'valid'   && <span className="rg-invite-status valid"><FiCheckCircle /></span>}
                  {!checkingInvite && inviteStatus === 'invalid' && <span className="rg-invite-status invalid"><FiX /></span>}
                  {!checkingInvite && inviteStatus === 'used'    && <span className="rg-invite-status invalid"><FiSlash /></span>}
                </div>
                {inviteStatus === 'valid'   && <span className="rg-invite-msg valid"><FiCheckCircle className="rg-msg-icon" /> Código válido — quedarás habilitado como vendedor</span>}
                {inviteStatus === 'invalid' && <span className="rg-invite-msg invalid"><FiX className="rg-msg-icon" /> Código inválido</span>}
                {inviteStatus === 'used'    && <span className="rg-invite-msg invalid"><FiSlash className="rg-msg-icon" /> Este código ya fue utilizado</span>}
                {!inviteStatus && <span className="rg-hint">Si fuiste invitado como vendedor, ingresá tu código aquí.</span>}
              </div>

              <div className="rg-field">
                <label className="rg-label"><FiGift className="rg-label-icon" /> Código de referido <span className="rg-optional">(opcional)</span></label>
                <input
                  className="rg-input"
                  placeholder="Ej: BITPLIFY100"
                  value={form.referral}
                  onChange={e => set('referral', e.target.value.toUpperCase())}
                />
                <span className="rg-hint">Si un amigo te invitó, ingresa su código y ambos ganan BP Coins.</span>
              </div>

              <div className="rg-summary">
                <div className="rg-summary-row"><span className="fin-label"><FiUser className="rg-sum-icon" /> Nombre</span><strong>{form.name}</strong></div>
                <div className="rg-summary-row"><span className="fin-label"><FaUserAstronaut className="rg-sum-icon" /> Trader</span><strong>@{form.username}</strong></div>
                <div className="rg-summary-row"><span className="fin-label"><FiGlobe className="rg-sum-icon" /> País</span><strong>{selectedCountry.flag} {selectedCountry.label}</strong></div>
                <div className="rg-summary-row"><span className="fin-label"><FiTrendingUp className="rg-sum-icon" /> Cripto</span><strong className="fin-num">{form.favCrypto}</strong></div>
                {inviteStatus === 'valid' && (
                  <div className="rg-summary-row">
                    <span className="fin-label"><FiKey className="rg-sum-icon" /> Acceso</span>
                    <strong>
                      <span className="fin-chip rg-chip-verified"><FiUserCheck className="rg-chip-icon" /> Vendedor verificado</span>
                    </strong>
                  </div>
                )}
              </div>
            </div>
          )}

          {step === 4 && (
            <div className="rg-success">
              <div className="rg-success-badge"><FaUserAstronaut /></div>
              <span className="fin-chip rg-success-tag"><span className="fin-num">NV 1</span> · Desbloqueado</span>
              <h2 className="rg-success-title">¡Nivel 1 Desbloqueado!</h2>
              <p className="rg-success-sub">Bienvenido a Bitplify, <strong>@{form.username}</strong>. Tu cuenta está lista.</p>
              <div className="rg-success-rewards">
                <div className="rg-reward">
                  <span className="rg-reward-icon"><FaCoins /></span>
                  <span className="fin-num rg-reward-val">+200</span>
                  <span className="rg-reward-txt">BP Coins de bienvenida</span>
                </div>
                <div className="rg-reward">
                  <span className="rg-reward-icon"><FiZap /></span>
                  <span className="fin-num rg-reward-val">+500</span>
                  <span className="rg-reward-txt">XP iniciales</span>
                </div>
                <div className="rg-reward">
                  <span className="rg-reward-icon"><FiGift /></span>
                  <span className="fin-num rg-reward-val">0%</span>
                  <span className="rg-reward-txt">comisión en tus primeros 3 trades</span>
                </div>
                {inviteStatus === 'valid' && (
                  <div className="rg-reward">
                    <span className="rg-reward-icon"><FiUserCheck /></span>
                    <span className="rg-reward-txt">Acceso de vendedor verificado activado</span>
                  </div>
                )}
              </div>
              <button className="rg-btn-primary" onClick={() => navigate('/')}>
                <FiShoppingBag /> Ir al Marketplace <FiArrowRight />
              </button>
            </div>
          )}

          {error && step < 4 && <div className="rg-error"><FiAlertTriangle className="rg-error-icon" /> {error}</div>}

          {step < 4 && (
            <div className="rg-nav">
              {step > 1 && (
                <button type="button" className="rg-btn-back" onClick={() => { setStep(s => s - 1); setError(''); }}>
                  <FiArrowLeft /> Atrás
                </button>
              )}
              {step < 3 ? (
                <button type="button" className="rg-btn-next" onClick={handleNext}>Siguiente <FiArrowRight /></button>
              ) : (
                <button type="button" className="rg-btn-primary" onClick={handleSubmit} disabled={loading}>
                  {loading ? <span className="rg-spinner" /> : <><FaUserAstronaut /> Crear cuenta</>}
                </button>
              )}
            </div>
          )}

          {step < 4 && (
            <p className="rg-login-link">
              ¿Ya tienes cuenta? <Link to="/login">Inicia sesión →</Link>
            </p>
          )}

        </div>
      </div>
    </div>
  );
}